import {
  createClient,
  type AuthChangeEvent,
  type AuthOAuthAuthorizationDetailsResponse,
  type AuthOAuthConsentResponse,
  type AuthResponse,
  type AuthTokenResponse,
  type AuthTokenResponsePassword,
  type OAuthResponse,
  type Provider,
  type SSOResponse,
  type Session,
  type Subscription,
  type UserResponse
} from "@supabase/supabase-js";
import { RealtimeClient } from "@supabase/realtime-js";
import {
  isAgentActivitySnapshotV1,
  type AgentActivitySnapshotV1,
  AgentAnswerDeltaEventSchema,
  AgentThinkingEventSchema,
  KnowledgeExpertProgressEventSchema,
  KnowledgeExpertToolEventSchema,
  PptExpertProgressEventSchema,
  PptExpertToolEventSchema,
  ExcelExpertProgressEventSchema,
  ExcelExpertToolEventSchema,
  BrowserExpertProgressEventSchema,
  BrowserExpertToolEventSchema,
  FRONTEND_TOOL_REALTIME_EVENTS,
  BROWSER_CONTROL_DEVICE_TOPIC_PREFIX,
  browserControlDeviceTopic,
  FrontendToolProbeSchema,
  UserAutomationBrowserProofSchema, type UserAutomationBrowserProof,
  FrontendToolReadySchema,
  FrontendToolRequestSchema,
  FrontendToolResponseSchema,
  FrontendToolCancelSchema,
  MATRIX_ROOM_ID_PATTERN,
  ThreadTitleProjectionSchema,
  type AgentAnswerDeltaEventV1,
  type AgentThinkingEventV1,
  type KnowledgeExpertProgressEventV1,
  type KnowledgeExpertToolEventV1,
  type PptExpertProgressEventV1,
  type PptExpertToolEventV1,
  type ExcelExpertProgressEventV1,
  type ExcelExpertToolEventV1,
  type BrowserExpertProgressEventV1,
  type BrowserExpertToolEventV1,
  type FrontendToolProbeV1,
  type FrontendToolReadyV1,
  type FrontendToolRequestV1,
  type FrontendToolResponseV1,
  type FrontendToolCancelV1,
  type ThreadTitleProjectionV1
} from "@matrix-idp/api-contracts";
import { Value } from "@sinclair/typebox/value";

const AGENT_PROGRESS_EVENT = "agent.progress.v1";
const AGENT_TOOL_EVENT = "agent.tool.v1";
const AGENT_ANSWER_DELTA_EVENT = "agent.answer.delta.v1";
const AGENT_THINKING_EVENT = "agent.thinking.v1";
const AGENT_ACTIVITY_SNAPSHOT_EVENT = "agent.activity.snapshot.v1";
const THREAD_TITLE_UPDATED_EVENT = "thread.title.updated.v1";
const matrixRoomIdPattern = new RegExp(MATRIX_ROOM_ID_PATTERN, "u");

const customOAuthProviderPattern = /^custom:[a-z0-9](?:[a-z0-9:-]{0,41}[a-z0-9])?$/u;

export type MatrixOAuthProvider = `custom:${string}`;

export function parseMatrixOAuthProvider(value: string): MatrixOAuthProvider {
  const provider = value.trim();
  if (customOAuthProviderPattern.test(provider)) return provider as MatrixOAuthProvider;
  throw new TypeError("Supabase OAuth provider must be a valid custom:<id>");
}

export interface MatrixSupabaseClientOptions {
  url: string;
  publishableKey: string;
  storage?: Storage;
}

export interface SupabaseOAuthAuthorization {
  authorizationUrl: string;
  state: string;
  codeVerifier: string;
}

export interface SupabaseOAuthTokenSet {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface SupabaseOAuthPublicClient {
  beginAuthorization(): Promise<SupabaseOAuthAuthorization>;
  exchangeCode(code: string, codeVerifier: string): Promise<SupabaseOAuthTokenSet>;
  refresh(refreshToken: string): Promise<SupabaseOAuthTokenSet>;
  signOut(accessToken: string): Promise<void>;
}

export interface SupabaseOAuthPublicClientOptions {
  url: string;
  publishableKey: string;
  clientId: string;
  redirectUri: string;
  fetch?: typeof fetch;
  now?: () => number;
}

export class SupabaseOAuthError extends Error {
  readonly operation: "exchange" | "refresh" | "sign-out";
  readonly status: number | null;

  constructor(
    operation: "exchange" | "refresh" | "sign-out",
    status: number | null,
    message: string
  ) {
    super(message);
    this.name = "SupabaseOAuthError";
    this.operation = operation;
    this.status = status;
  }
}

export function createSupabaseOAuthPublicClient(
  options: SupabaseOAuthPublicClientOptions
): SupabaseOAuthPublicClient {
  const baseUrl = authBaseUrl(options.url);
  const redirectUri = applicationCallbackUrl(options.redirectUri);
  const clientId = uuid(options.clientId, "Supabase OAuth client id");
  const publishableKey = requiredValue(options.publishableKey, "Supabase publishable key");
  const fetchImpl = options.fetch ?? fetch;
  const now = options.now ?? Date.now;

  async function requestToken(
    operation: "exchange" | "refresh",
    body: URLSearchParams,
    previousRefreshToken?: string
  ): Promise<SupabaseOAuthTokenSet> {
    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}/oauth/token`, {
        method: "POST",
        headers: {
          accept: "application/json",
          apikey: publishableKey,
          "content-type": "application/x-www-form-urlencoded"
        },
        body,
        credentials: "omit",
        redirect: "error"
      });
    } catch {
      throw new SupabaseOAuthError(operation, null, "Supabase OAuth endpoint is unavailable");
    }
    const value: unknown = await response.json().catch(() => null);
    if (!response.ok || !isOAuthTokenResponse(value)) {
      throw new SupabaseOAuthError(
        operation,
        response.status,
        "Supabase OAuth token request failed"
      );
    }
    const refreshToken = value.refresh_token ?? previousRefreshToken;
    if (!refreshToken) {
      throw new SupabaseOAuthError(
        operation,
        response.status,
        "Supabase did not issue a refresh token"
      );
    }
    let tokenExpiresAt: number;
    try {
      tokenExpiresAt = readSupabaseAccessTokenExpiresAt(value.access_token);
    } catch {
      throw new SupabaseOAuthError(
        operation,
        response.status,
        "Supabase issued an invalid access token"
      );
    }
    return {
      accessToken: value.access_token,
      refreshToken,
      expiresAt: Math.min(Math.floor(now() / 1000) + value.expires_in, tokenExpiresAt)
    };
  }

  return {
    async beginAuthorization() {
      const codeVerifier = randomBase64Url(64);
      const state = randomBase64Url(32);
      const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
      const query = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        code_challenge: base64Url(digest),
        code_challenge_method: "S256",
        scope: "openid profile email",
        state
      });
      return {
        authorizationUrl: `${baseUrl}/oauth/authorize?${query.toString()}`,
        state,
        codeVerifier
      };
    },

    exchangeCode(code, codeVerifier) {
      return requestToken(
        "exchange",
        new URLSearchParams({
          grant_type: "authorization_code",
          client_id: clientId,
          redirect_uri: redirectUri,
          code: requiredValue(code, "OAuth authorization code"),
          code_verifier: requiredValue(codeVerifier, "OAuth PKCE verifier")
        })
      );
    },

    refresh(refreshToken) {
      const token = requiredValue(refreshToken, "OAuth refresh token");
      return requestToken(
        "refresh",
        new URLSearchParams({
          grant_type: "refresh_token",
          client_id: clientId,
          refresh_token: token
        }),
        token
      );
    },

    async signOut(accessToken) {
      let response: Response;
      try {
        response = await fetchImpl(`${baseUrl}/logout?scope=local`, {
          method: "POST",
          headers: {
            apikey: publishableKey,
            authorization: `Bearer ${requiredValue(accessToken, "OAuth access token")}`
          },
          credentials: "omit",
          redirect: "error"
        });
      } catch {
        throw new SupabaseOAuthError("sign-out", null, "Supabase sign-out endpoint is unavailable");
      }
      if (!response.ok) {
        throw new SupabaseOAuthError("sign-out", response.status, "Supabase sign-out failed");
      }
    }
  };
}

interface OAuthTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

function isOAuthTokenResponse(value: unknown): value is OAuthTokenResponse {
  if (!value || typeof value !== "object") return false;
  const token = value as Record<string, unknown>;
  return (
    typeof token.access_token === "string" &&
    token.access_token.length > 0 &&
    (token.refresh_token === undefined ||
      (typeof token.refresh_token === "string" && token.refresh_token.length > 0)) &&
    typeof token.expires_in === "number" &&
    Number.isSafeInteger(token.expires_in) &&
    token.expires_in > 0 &&
    typeof token.token_type === "string" &&
    token.token_type.toLowerCase() === "bearer"
  );
}

export function readSupabaseAccessTokenExpiresAt(accessToken: string): number {
  const segments = accessToken.split(".");
  if (segments.length !== 3 || !segments[1]) {
    throw new TypeError("Supabase access token must be a JWT");
  }
  let claims: unknown;
  try {
    const encoded = segments[1].replace(/-/gu, "+").replace(/_/gu, "/");
    const padded = encoded.padEnd(encoded.length + ((4 - (encoded.length % 4)) % 4), "=");
    claims = JSON.parse(atob(padded));
  } catch {
    throw new TypeError("Supabase access token claims are invalid");
  }
  if (!claims || typeof claims !== "object" || Array.isArray(claims)) {
    throw new TypeError("Supabase access token claims are invalid");
  }
  const expiresAt = (claims as Record<string, unknown>).exp;
  if (!Number.isSafeInteger(expiresAt) || Number(expiresAt) <= 0) {
    throw new TypeError("Supabase access token expiry is invalid");
  }
  return Number(expiresAt);
}

function authBaseUrl(value: string): string {
  const url = new URL(value);
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) {
    throw new TypeError("Supabase URL must be HTTP(S) and contain no credentials");
  }
  if (url.search || url.hash) throw new TypeError("Supabase URL must not include query or hash");
  return `${url.origin}${url.pathname.replace(/\/$/u, "")}/auth/v1`;
}

function applicationCallbackUrl(value: string): string {
  const url = new URL(value);
  const loopback = url.protocol === "http:" && url.hostname === "127.0.0.1" && Boolean(url.port);
  const secureCoordinator = url.protocol === "https:";
  if ((!loopback && !secureCoordinator) || url.username || url.password || url.hash) {
    throw new TypeError("Workbench OAuth callback must use HTTPS or an IPv4 loopback URL");
  }
  return url.toString();
}

function uuid(value: string, name: string): string {
  const parsed = requiredValue(value, name);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(parsed)) {
    throw new TypeError(`${name} must be a UUID`);
  }
  return parsed;
}

function requiredValue(value: string, name: string): string {
  const parsed = value.trim();
  if (!parsed || parsed.length > 4096 || /[\r\n]/u.test(parsed)) {
    throw new TypeError(`${name} is invalid`);
  }
  return parsed;
}

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function randomBase64Url(bytes: number): string {
  const value = new Uint8Array(bytes);
  crypto.getRandomValues(value);
  return base64Url(value.buffer);
}

function base64Url(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/gu, "");
}

export interface MatrixSupabaseClient {
  auth: {
    signInWithPassword(email: string, password: string): Promise<AuthTokenResponsePassword>;
    requestPasswordRecovery(
      email: string,
      redirectTo: string
    ): Promise<{ data: unknown; error: Error | null }>;
    signInWithOAuth(provider: MatrixOAuthProvider, redirectTo: string): Promise<OAuthResponse>;
    signInWithSaml(domain: string, redirectTo: string): Promise<SSOResponse>;
    exchangeCodeForSession(code: string): Promise<AuthTokenResponse>;
    establishSession(accessToken: string, refreshToken: string): Promise<AuthResponse>;
    updatePassword(password: string): Promise<UserResponse>;
    getUser(): Promise<UserResponse>;
    refreshSession(): Promise<AuthResponse>;
    signOut(): Promise<{ error: Error | null }>;
    onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void): {
      data: { subscription: Subscription };
    };
    getAuthorizationDetails(
      authorizationId: string
    ): Promise<AuthOAuthAuthorizationDetailsResponse>;
    approveAuthorization(authorizationId: string): Promise<AuthOAuthConsentResponse>;
    denyAuthorization(authorizationId: string): Promise<AuthOAuthConsentResponse>;
  };
  uploadToSignedUrl(input: {
    bucket: "temporary-files";
    objectKey: string;
    uploadToken: string;
    body: Blob;
    mediaType: string;
  }): Promise<{
    data: { path: string; fullPath: string } | null;
    error: Error | null;
  }>;
  downloadSignedUrl(url: string, signal?: AbortSignal): Promise<Response>;
  subscribeToAgentRooms(input: {
    roomIds: readonly string[];
    browserControlDeviceIds?: readonly string[];
    token: string;
    onEvent(observation: AgentRealtimeObservation): void;
    onStatus?(
      roomId: string,
      status: "subscribed" | "closed" | "channel_error" | "timed_out"
    ): void;
    onBrowserControlStatus?(
      deviceId: string,
      status: "subscribed" | "closed" | "channel_error" | "timed_out"
    ): void;
  }): Promise<{
    updateToken(token: string): Promise<void>;
    updateRooms(roomIds: readonly string[]): Promise<void>;
    updateBrowserControlDevices(deviceIds: readonly string[]): Promise<void>;
    unsubscribe(): Promise<void>;
  }>;
}

export type FrontendToolRealtimeEvent =
  (typeof FRONTEND_TOOL_REALTIME_EVENTS)[keyof typeof FRONTEND_TOOL_REALTIME_EVENTS];
export type FrontendToolRealtimePayload =
  | UserAutomationBrowserProof
  | FrontendToolProbeV1
  | FrontendToolReadyV1
  | FrontendToolRequestV1
  | FrontendToolResponseV1
  | FrontendToolCancelV1;

export type AgentRealtimeObservation =
  | {
      kind: "progress";
      roomId: string;
      event: KnowledgeExpertProgressEventV1 | PptExpertProgressEventV1 | ExcelExpertProgressEventV1 | BrowserExpertProgressEventV1;
    }
  | {
      kind: "tool";
      roomId: string;
      event: KnowledgeExpertToolEventV1 | PptExpertToolEventV1 | ExcelExpertToolEventV1 | BrowserExpertToolEventV1;
    }
  | { kind: "answerDelta"; roomId: string; event: AgentAnswerDeltaEventV1 }
  | { kind: "thinking"; roomId: string; event: AgentThinkingEventV1 }
  | { kind: "activitySnapshot"; roomId: string; event: AgentActivitySnapshotV1 }
  | { kind: "threadTitle"; roomId: string; event: ThreadTitleProjectionV1 }
  | { kind: "browserTool"; roomId: string; wireEvent: FrontendToolRealtimeEvent; payload: FrontendToolRealtimePayload };

export function createMatrixSupabaseClient(
  options: MatrixSupabaseClientOptions
): MatrixSupabaseClient {
  const client = createClient(options.url, options.publishableKey, {
    auth: {
      flowType: "pkce",
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      ...(options.storage ? { storage: options.storage } : {})
    }
  });

  return {
    auth: {
      signInWithPassword: (email: string, password: string) =>
        client.auth.signInWithPassword({ email, password }),
      requestPasswordRecovery: (email: string, redirectTo: string) =>
        client.auth.resetPasswordForEmail(email, { redirectTo }),
      signInWithOAuth: (provider: MatrixOAuthProvider, redirectTo: string) =>
        client.auth.signInWithOAuth({
          provider: provider as Provider,
          options: { redirectTo, skipBrowserRedirect: false }
        }),
      signInWithSaml: (domain: string, redirectTo: string) =>
        client.auth.signInWithSSO({ domain, options: { redirectTo } }),
      exchangeCodeForSession: (code: string) => client.auth.exchangeCodeForSession(code),
      establishSession: (accessToken: string, refreshToken: string) =>
        client.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }),
      updatePassword: (password: string) => client.auth.updateUser({ password }),
      getUser: () => client.auth.getUser(),
      refreshSession: () => client.auth.refreshSession(),
      signOut: () => client.auth.signOut({ scope: "local" }),
      onAuthStateChange: (callback: (event: AuthChangeEvent, session: Session | null) => void) =>
        client.auth.onAuthStateChange(callback),
      getAuthorizationDetails: (authorizationId: string) =>
        client.auth.oauth.getAuthorizationDetails(authorizationId),
      approveAuthorization: (authorizationId: string) =>
        client.auth.oauth.approveAuthorization(authorizationId),
      denyAuthorization: (authorizationId: string) =>
        client.auth.oauth.denyAuthorization(authorizationId)
    },
    async uploadToSignedUrl(input: {
      bucket: "temporary-files";
      objectKey: string;
      uploadToken: string;
      body: Blob;
      mediaType: string;
    }) {
      const { data, error } = await client.storage
        .from(input.bucket)
        .uploadToSignedUrl(input.objectKey, input.uploadToken, input.body, {
          contentType: input.mediaType
        });
      return { data, error };
    },
    downloadSignedUrl(url: string, signal?: AbortSignal) {
      return fetch(url, { credentials: "omit", ...(signal ? { signal } : {}) });
    },
    async subscribeToAgentRooms(input) {
      const roomIds = agentRealtimeRoomIds(input.roomIds);
      const deviceIds = browserControlDeviceIds(input.browserControlDeviceIds ?? []);
      let currentToken = requiredValue(input.token, "Agent Realtime token");
      const publishableKey = requiredValue(options.publishableKey, "Supabase publishable key");
      const realtime = new RealtimeClient(realtimeEndpoint(options.url), {
        params: { apikey: publishableKey }
      });
      await realtime.setAuth(currentToken);
      const createChannel = (roomId: string) =>
        realtime
          .channel(`matrix-room:${roomId}`, {
            config: { private: true, broadcast: { ack: true, self: false } }
          })
          .on("broadcast", { event: AGENT_PROGRESS_EVENT }, ({ payload }) => {
            if (
              Value.Check(KnowledgeExpertProgressEventSchema, payload) ||
              Value.Check(PptExpertProgressEventSchema, payload) ||
              Value.Check(ExcelExpertProgressEventSchema, payload) ||
              Value.Check(BrowserExpertProgressEventSchema, payload)
            ) {
              input.onEvent({ kind: "progress", roomId, event: payload });
            }
          })
          .on("broadcast", { event: AGENT_TOOL_EVENT }, ({ payload }) => {
            if (
              Value.Check(KnowledgeExpertToolEventSchema, payload) ||
              Value.Check(PptExpertToolEventSchema, payload) ||
              Value.Check(ExcelExpertToolEventSchema, payload) ||
              Value.Check(BrowserExpertToolEventSchema, payload)
            ) {
              input.onEvent({ kind: "tool", roomId, event: payload });
            }
          })
          .on("broadcast", { event: AGENT_ANSWER_DELTA_EVENT }, ({ payload }) => {
            if (Value.Check(AgentAnswerDeltaEventSchema, payload)) {
              input.onEvent({ kind: "answerDelta", roomId, event: payload });
            }
          })
          .on("broadcast", { event: AGENT_THINKING_EVENT }, ({ payload }) => {
            if (Value.Check(AgentThinkingEventSchema, payload)) {
              input.onEvent({ kind: "thinking", roomId, event: payload });
            }
          })
          .on("broadcast", { event: AGENT_ACTIVITY_SNAPSHOT_EVENT }, ({ payload }) => {
            if (isAgentActivitySnapshotV1(payload)) {
              input.onEvent({ kind: "activitySnapshot", roomId, event: payload });
            }
          })
          .on("broadcast", { event: THREAD_TITLE_UPDATED_EVENT }, ({ payload }) => {
            if (Value.Check(ThreadTitleProjectionSchema, payload)) {
              input.onEvent({ kind: "threadTitle", roomId, event: payload });
            }
          })
          .on("broadcast", { event: FRONTEND_TOOL_REALTIME_EVENTS.automationCommand }, ({ payload }) => {
            publishBrowserToolObservation(input.onEvent, FRONTEND_TOOL_REALTIME_EVENTS.automationCommand, payload, roomId);
          })
          .on("broadcast", { event: FRONTEND_TOOL_REALTIME_EVENTS.automationReply }, ({ payload }) => {
            publishBrowserToolObservation(input.onEvent, FRONTEND_TOOL_REALTIME_EVENTS.automationReply, payload, roomId);
          });
      const createBrowserControlDeviceChannel = (deviceId: string) =>
        realtime
          .channel(browserControlDeviceTopic(deviceId), {
            config: { private: true, broadcast: { ack: true, self: false } }
          })
          .on("broadcast", { event: FRONTEND_TOOL_REALTIME_EVENTS.probe }, ({ payload }) => {
            publishBrowserToolObservation(input.onEvent, FRONTEND_TOOL_REALTIME_EVENTS.probe, payload);
          })
          .on("broadcast", { event: FRONTEND_TOOL_REALTIME_EVENTS.request }, ({ payload }) => {
            publishBrowserToolObservation(input.onEvent, FRONTEND_TOOL_REALTIME_EVENTS.request, payload);
          })
          .on("broadcast", { event: FRONTEND_TOOL_REALTIME_EVENTS.cancel }, ({ payload }) => {
            publishBrowserToolObservation(input.onEvent, FRONTEND_TOOL_REALTIME_EVENTS.cancel, payload);
          });
      const channels = new Map<string, ReturnType<typeof createChannel>>();
      const browserDeviceChannels = new Map<string, ReturnType<typeof createBrowserControlDeviceChannel>>();
      const join = async (
        channel: ReturnType<typeof createChannel>,
        onStatus: (status: "subscribed" | "closed" | "channel_error" | "timed_out") => void
      ): Promise<void> => {
        await new Promise<void>((resolve, reject) => {
          let subscribed = false;
          channel.subscribe((status) => {
            const normalized = realtimeStatus(status);
            if (normalized) onStatus(normalized);
            if (status === "SUBSCRIBED" && !subscribed) {
              subscribed = true;
              resolve();
            } else if (
              !subscribed &&
              (status === "CLOSED" || status === "CHANNEL_ERROR" || status === "TIMED_OUT")
            ) {
              reject(new Error(`Agent Realtime subscription failed: ${status}`));
            }
          });
        });
      };
      const joinRoom = async (roomId: string): Promise<void> => {
        const channel = createChannel(roomId);
        channels.set(roomId, channel);
        try {
          await join(channel, (status) => input.onStatus?.(roomId, status));
        } catch (error) {
          channels.delete(roomId);
          await realtime.removeChannel(channel).catch(() => undefined);
          throw error;
        }
      };
      const joinBrowserControlDevice = async (deviceId: string): Promise<void> => {
        const channel = createBrowserControlDeviceChannel(deviceId);
        browserDeviceChannels.set(deviceId, channel);
        try {
          await join(channel, (status) => input.onBrowserControlStatus?.(deviceId, status));
        } catch (error) {
          browserDeviceChannels.delete(deviceId);
          await realtime.removeChannel(channel).catch(() => undefined);
          throw error;
        }
      };
      const updateRooms = async (nextRoomIds: readonly string[]): Promise<void> => {
        const next = agentRealtimeRoomIds(nextRoomIds);
        const nextSet = new Set(next);
        const additions = next.filter((roomId) => !channels.has(roomId));
        const removals = [...channels.keys()].filter((roomId) => !nextSet.has(roomId));
        try {
          await Promise.all(additions.map(joinRoom));
        } catch (error) {
          await Promise.all(
            additions.map(async (roomId) => {
              const channel = channels.get(roomId);
              if (!channel) return;
              channels.delete(roomId);
              await realtime.removeChannel(channel).catch(() => undefined);
            })
          );
          throw error;
        }
        await Promise.all(
          removals.map(async (roomId) => {
            const channel = channels.get(roomId);
            if (!channel) return;
            channels.delete(roomId);
            await realtime.removeChannel(channel);
          })
        );
      };
      const updateBrowserControlDevices = async (nextDeviceIds: readonly string[]): Promise<void> => {
        const next = browserControlDeviceIds(nextDeviceIds);
        const nextSet = new Set(next);
        const additions = next.filter((deviceId) => !browserDeviceChannels.has(deviceId));
        const removals = [...browserDeviceChannels.keys()].filter((deviceId) => !nextSet.has(deviceId));
        try {
          await Promise.all(additions.map(joinBrowserControlDevice));
        } catch (error) {
          await Promise.all(
            additions.map(async (deviceId) => {
              const channel = browserDeviceChannels.get(deviceId);
              if (!channel) return;
              browserDeviceChannels.delete(deviceId);
              await realtime.removeChannel(channel).catch(() => undefined);
            })
          );
          throw error;
        }
        await Promise.all(
          removals.map(async (deviceId) => {
            const channel = browserDeviceChannels.get(deviceId);
            if (!channel) return;
            browserDeviceChannels.delete(deviceId);
            await realtime.removeChannel(channel);
          })
        );
      };
      try {
        await updateRooms(roomIds);
        await updateBrowserControlDevices(deviceIds);
      } catch (error) {
        await Promise.all(
          [...channels.values(), ...browserDeviceChannels.values()].map((channel) =>
            realtime.removeChannel(channel).catch(() => undefined)
          )
        );
        await realtime.disconnect().catch(() => undefined);
        throw error;
      }
      let closed = false;
      return {
        async updateToken(nextToken: string) {
          if (closed) throw new Error("Agent Realtime subscription is closed");
          currentToken = requiredValue(nextToken, "Agent Realtime token");
          await realtime.setAuth(currentToken);
        },
        async updateRooms(nextRoomIds: readonly string[]) {
          if (closed) throw new Error("Agent Realtime subscription is closed");
          await updateRooms(nextRoomIds);
        },
        async updateBrowserControlDevices(nextDeviceIds: readonly string[]) {
          if (closed) throw new Error("Agent Realtime subscription is closed");
          await updateBrowserControlDevices(nextDeviceIds);
        },
        async unsubscribe() {
          if (closed) return;
          closed = true;
          try {
            await Promise.all(
              [...channels.values(), ...browserDeviceChannels.values()].map((channel) =>
                realtime.removeChannel(channel)
              )
            );
            channels.clear();
            browserDeviceChannels.clear();
          } finally {
            await realtime.disconnect();
          }
        }
      };
    }
  };
}

function browserToolWireSchema(event: FrontendToolRealtimeEvent) {
  if (event === FRONTEND_TOOL_REALTIME_EVENTS.automationCommand || event === FRONTEND_TOOL_REALTIME_EVENTS.automationReply) return UserAutomationBrowserProofSchema;
  if (event === FRONTEND_TOOL_REALTIME_EVENTS.probe) return FrontendToolProbeSchema;
  if (event === FRONTEND_TOOL_REALTIME_EVENTS.ready) return FrontendToolReadySchema;
  if (event === FRONTEND_TOOL_REALTIME_EVENTS.request) return FrontendToolRequestSchema;
  if (event === FRONTEND_TOOL_REALTIME_EVENTS.response) return FrontendToolResponseSchema;
  return FrontendToolCancelSchema;
}

function publishBrowserToolObservation(
  publish: (observation: AgentRealtimeObservation) => void,
  event: FrontendToolRealtimeEvent,
  payload: unknown,
  fallbackRoomId?: string
): void {
  const parsed = browserToolWireSchema(event).safeParse(payload);
  if (!parsed.success) return;
  const data = parsed.data as unknown;
  const roomId = record(data) && typeof data.roomId === "string"
    ? data.roomId
    : fallbackRoomId;
  if (roomId) publish({ kind: "browserTool", roomId, wireEvent: event, payload: parsed.data });
}

function realtimeEndpoint(value: string): string {
  const endpoint = new URL("realtime/v1", supabaseBaseUrl(value));
  endpoint.protocol = endpoint.protocol === "https:" ? "wss:" : "ws:";
  return endpoint.toString();
}

function absoluteSupabaseUrl(value: string): URL {
  const url = new URL(value);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new TypeError("Supabase URL must use HTTP or HTTPS");
  }
  return url;
}

function supabaseBaseUrl(value: string): URL {
  const base = absoluteSupabaseUrl(value);
  return new URL(base.toString().endsWith("/") ? base : `${base}/`);
}

function matrixRoomId(value: string): string {
  const roomId = requiredValue(value, "Matrix room id");
  if (!matrixRoomIdPattern.test(roomId)) throw new TypeError("Matrix room id is invalid");
  return roomId;
}

function agentRealtimeRoomIds(values: readonly string[]): string[] {
  const roomIds = values.map(matrixRoomId);
  if (roomIds.length === 0 || new Set(roomIds).size !== roomIds.length) {
    throw new TypeError("Agent Realtime rooms must be a non-empty unique list");
  }
  return roomIds;
}

function browserControlDeviceIds(values: readonly string[]): string[] {
  const deviceIds = values.map((value) =>
    browserControlDeviceTopic(requiredValue(value, "Browser control device id")).slice(
      BROWSER_CONTROL_DEVICE_TOPIC_PREFIX.length
    )
  );
  if (new Set(deviceIds).size !== deviceIds.length) {
    throw new TypeError("Browser control devices must be a unique list");
  }
  return deviceIds;
}

function realtimeStatus(
  value: string
): "subscribed" | "closed" | "channel_error" | "timed_out" | null {
  if (value === "SUBSCRIBED") return "subscribed";
  if (value === "CLOSED") return "closed";
  if (value === "CHANNEL_ERROR") return "channel_error";
  if (value === "TIMED_OUT") return "timed_out";
  return null;
}
